<script setup>
import { dt } from '@/utils/i18n';

</script>

<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <!-- Brand Section -->
        <div class="footer-brand">
          <h3 class="footer-logo">LawDosti</h3>
          <p class="footer-tagline">{{ dt('footer.tagline') }}</p>
          <div class="social-links">
            <a href="#" class="social-link" :aria-label="dt('footer.social.facebook')">📘</a>
            <a href="#" class="social-link" :aria-label="dt('footer.social.twitter')">🐦</a>
            <a href="#" class="social-link" :aria-label="dt('footer.social.linkedin')">💼</a>
            <a href="#" class="social-link" :aria-label="dt('footer.social.instagram')">📷</a>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="footer-section">
          <h4 class="footer-heading">{{ dt('footer.quickLinks.title') }}</h4>
          <ul class="footer-links">
            <li><a href="/search">{{ dt('footer.quickLinks.findLawyers') }}</a></li>
            <li><a href="/about">{{ dt('footer.quickLinks.about') }}</a></li>
            <li><a href="/how-it-works">{{ dt('footer.quickLinks.howItWorks') }}</a></li>
            <li><a href="/contact">{{ dt('footer.quickLinks.contact') }}</a></li>
          </ul>
        </div>

        <!-- Legal Areas -->
        <div class="footer-section">
          <h4 class="footer-heading">{{ dt('footer.practiceAreas.title') }}</h4>
          <ul class="footer-links">
            <li><a href="/search?area=family">{{ dt('footer.practiceAreas.family') }}</a></li>
            <li><a href="/search?area=property">{{ dt('footer.practiceAreas.property') }}</a></li>
            <li><a href="/search?area=criminal">{{ dt('footer.practiceAreas.criminal') }}</a></li>
            <li><a href="/search?area=corporate">{{ dt('footer.practiceAreas.corporate') }}</a></li>
          </ul>
        </div>

        <!-- Support -->
        <div class="footer-section">
          <h4 class="footer-heading">{{ dt('footer.support.title') }}</h4>
          <ul class="footer-links">
            <li><a href="/help">{{ dt('footer.support.help') }}</a></li>
            <li><a href="/faq">{{ dt('footer.support.faq') }}</a></li>
            <li><a href="/privacy">{{ dt('footer.support.privacy') }}</a></li>
            <li><a href="/terms">{{ dt('footer.support.terms') }}</a></li>
          </ul>
        </div>
      </div>

      <!-- Footer Bottom -->
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <p>{{ dt('footer.copyright', { year: new Date().getFullYear() }) }}</p>
          <div class="footer-bottom-links">
            <a href="/privacy">{{ dt('footer.bottomLinks.privacy') }}</a>
            <a href="/terms">{{ dt('footer.bottomLinks.terms') }}</a>
            <a href="/cookies">{{ dt('footer.bottomLinks.cookies') }}</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  color: white;
  padding: 0;
  margin-top: auto;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  padding: 4rem 0 2rem;
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 3rem 0 2rem;
  }
}

.footer-brand {
  max-width: 300px;
}

.footer-logo {
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: white;
  background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.footer-tagline {
  color: #e2e8f0;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.social-link:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.footer-heading {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;
  position: relative;
}

.footer-heading::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -8px;
  width: 30px;
  height: 2px;
  background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 0.75rem;
}

.footer-links a {
  color: #cbd5e0;
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 0.95rem;
}

.footer-links a:hover {
  color: white;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem 0;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

@media (max-width: 640px) {
  .footer-bottom-content {
    flex-direction: column;
    text-align: center;
  }
}

.footer-bottom-links {
  display: flex;
  gap: 2rem;
}

.footer-bottom-links a {
  color: #cbd5e0;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.footer-bottom-links a:hover {
  color: white;
}
</style>